(() => {
  'use strict';
  const { clamp, announce, downloadBlob } = window.PFUtils;
  const input = document.querySelector('#photo-input');
  const photoLabel = document.querySelector('#photo-label');
  const image = document.querySelector('#room-photo');
  const stage = document.querySelector('#photo-stage');
  const placeholder = document.querySelector('#photo-placeholder');
  const selectionBox = document.querySelector('#selection-box');
  const results = document.querySelector('#color-results');
  const controls = document.querySelector('#photo-controls');
  const error = document.querySelector('#photo-error');
  const modeButtons = [...document.querySelectorAll('[data-mode]')];
  let sourceCanvas = document.createElement('canvas');
  let sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
  let objectUrl = null;
  let activeMode = 'whole';
  let customRegion = null;
  let dragging = null;
  let currentPalette = [];
  let currentInsight = null;

  function hexToRgb(hex) {
    const value = hex.replace('#','');
    return [parseInt(value.slice(0,2),16),parseInt(value.slice(2,4),16),parseInt(value.slice(4,6),16)];
  }
  function rgbToHex(rgb) { return `#${rgb.map(value => clamp(Math.round(value),0,255).toString(16).padStart(2,'0')).join('')}`.toUpperCase(); }
  function distance(a,b) { return Math.hypot(a[0]-b[0],a[1]-b[1],a[2]-b[2]); }
  function nearestName(hex) {
    const rgb = hexToRgb(hex);
    const [h, s, l] = rgbToHsl(rgb);
    if (s < 8) {
      const [r,g,b] = rgb;
      const spread = Math.max(r,g,b) - Math.min(r,g,b);
      if (spread >= 7) {
        if (r > g + 3 && r > b + 5) return l < 42 ? 'Deep mauve gray' : l > 62 ? 'Light mauve gray' : 'Mauve gray';
        if (b > r + 5) return l > 62 ? 'Light blue gray' : 'Blue gray';
        if (g >= r && g > b + 5) return l < 42 ? 'Deep olive gray' : l > 62 ? 'Light olive gray' : 'Olive gray';
        if (r >= g && g > b + 5) return l > 62 ? 'Light warm gray' : 'Warm gray';
      }
      if (l > 92) return 'Soft white';
      if (l > 75) return 'Light gray';
      if (l > 45) return 'Stone gray';
      if (l > 20) return 'Charcoal';
      return 'Soft black';
    }
    if (h >= 345 || h < 15) return s < 24 ? (l > 58 ? 'Dusty rose' : 'Mauve taupe') : (l > 55 ? 'Clay rose' : 'Terracotta');
    if (h < 45) return s < 22 ? (l > 62 ? 'Warm beige' : 'Warm taupe') : (l > 58 ? 'Caramel' : 'Walnut');
    if (h < 75) return s < 14 ? 'Warm gray' : (l > 58 ? 'Soft olive' : 'Muted olive');
    if (h < 165) return l > 60 ? 'Soft sage' : (l > 38 ? 'Sage' : 'Forest green');
    if (h < 205) return l > 55 ? 'Dusty teal' : 'Muted teal';
    if (h < 255) return s < 16 ? 'Slate gray' : (l > 55 ? 'Dusty blue' : 'Slate blue');
    if (h < 300) return s < 18 ? 'Lavender gray' : 'Muted lavender';
    return s < 22 ? 'Dusty mauve' : 'Muted berry';
  }
  function rgbToHsl([r,g,b]) {
    r/=255;g/=255;b/=255;const max=Math.max(r,g,b),min=Math.min(r,g,b);let h=0,s=0;const l=(max+min)/2;
    if(max!==min){const d=max-min;s=l>.5?d/(2-max-min):d/(max+min);switch(max){case r:h=(g-b)/d+(g<b?6:0);break;case g:h=(b-r)/d+2;break;default:h=(r-g)/d+4}h*=60}
    return [h,s*100,l*100];
  }
  function hslToRgb([h,s,l]) {
    h=((h%360)+360)%360;s/=100;l/=100;const c=(1-Math.abs(2*l-1))*s;const x=c*(1-Math.abs((h/60)%2-1));const m=l-c/2;let p;
    if(h<60)p=[c,x,0];else if(h<120)p=[x,c,0];else if(h<180)p=[0,c,x];else if(h<240)p=[0,x,c];else if(h<300)p=[x,0,c];else p=[c,0,x];
    return p.map(v=>(v+m)*255);
  }
  function luminance([r,g,b]) { return .2126*r/255 + .7152*g/255 + .0722*b/255; }

  function regionForMode() {
    if (activeMode === 'custom' && customRegion) return customRegion;
    return { x:0,y:0,w:1,h:1 };
  }

  function renderSelection(region) {
    if (!image.naturalWidth || activeMode === 'whole') { selectionBox.classList.add('is-hidden'); return; }
    const stageRect = stage.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    selectionBox.style.left = `${imageRect.left-stageRect.left+region.x*imageRect.width}px`;
    selectionBox.style.top = `${imageRect.top-stageRect.top+region.y*imageRect.height}px`;
    selectionBox.style.width = `${region.w*imageRect.width}px`;
    selectionBox.style.height = `${region.h*imageRect.height}px`;
    selectionBox.classList.remove('is-hidden');
  }

  function extractPalette(region) {
    const sample = document.createElement('canvas');
    sample.width = 120; sample.height = 120;
    const ctx = sample.getContext('2d', { willReadFrequently:true });
    const sx = Math.round(region.x * sourceCanvas.width), sy = Math.round(region.y * sourceCanvas.height);
    const sw = Math.max(1,Math.round(region.w * sourceCanvas.width)), sh = Math.max(1,Math.round(region.h * sourceCanvas.height));
    ctx.drawImage(sourceCanvas,sx,sy,sw,sh,0,0,120,120);
    const pixels = ctx.getImageData(0,0,120,120).data;
    const buckets = new Map();
    for(let i=0;i<pixels.length;i+=4){
      if(pixels[i+3]<200)continue;
      const r=Math.round(pixels[i]/8)*8,g=Math.round(pixels[i+1]/8)*8,b=Math.round(pixels[i+2]/8)*8;
      const key=`${clamp(r,0,255)},${clamp(g,0,255)},${clamp(b,0,255)}`;
      buckets.set(key,(buckets.get(key)||0)+1);
    }
    const ranked=[...buckets.entries()].sort((a,b)=>b[1]-a[1]);
    const chosen=[];
    for(const [key,count] of ranked){
      const rgb=key.split(',').map(Number);
      if(chosen.every(item=>distance(item.rgb,rgb)>18)){chosen.push({rgb,count});if(chosen.length===5)break}
    }
    const totals=chosen.map(()=>0);
    for(const [key,count] of ranked){
      const rgb=key.split(',').map(Number);
      let best=0,bestDistance=Infinity;
      chosen.forEach((item,index)=>{const d=distance(item.rgb,rgb);if(d<bestDistance){best=index;bestDistance=d}});
      totals[best]+=count;
    }
    const totalPixels=totals.reduce((sum,count)=>sum+count,0)||1;
    return chosen.map((item,index)=>({hex:rgbToHex(item.rgb),rgb:item.rgb,count:totals[index],share:Math.round(totals[index]/totalPixels*100),name:nearestName(rgbToHex(item.rgb))})).sort((a,b)=>b.count-a.count);
  }

  function buildInsight(palette) {
    const dominant=palette[0];
    const [h,s,l]=rgbToHsl(dominant.rgb);
    const temperature=s<9?'neutral':(h<75||h>330)?'warm':(h>=75&&h<250)?'cool':'balanced';
    const light=l>72?'light':l<35?'deep':'mid-tone';
    const mood=temperature==='warm'?'A warm, grounded foundation.':temperature==='cool'?'A cool, composed foundation.':temperature==='neutral'?'A quiet, neutral foundation.':'A balanced color foundation.';
    const harmony=[h-18,h+16,h+34].map((angle,index)=>rgbToHex(hslToRgb([angle,clamp(s*(index===2?.65:.8),12,48),clamp(l+(index-1)*10,24,82)])));
    const contrast=[h+180,h+155,h+205].map((angle,index)=>rgbToHex(hslToRgb([angle,clamp(s+18,28,58),clamp(l+(index-1)*6,30,70)])));
    const lum=luminance(dominant.rgb);
    let frame;
    if(lum>.72) frame=temperature==='warm'?'Soft black gives definition; oak or walnut keeps the warmth.':'Black gives crisp contrast; pale oak keeps it lighter.';
    else if(lum<.32) frame='Warm white or pale oak will separate clearly from this darker surface.';
    else frame=temperature==='warm'?'Walnut, oak or soft black will sit naturally with this mid-tone wall.':'White, black or pale oak will give a clean edge against this wall.';
    return {dominant,h,s,l,temperature,light,mood,harmony,contrast,frame};
  }

  function swatchButton(item) {
    const rgbText=`rgb(${item.rgb.join(', ')})`;
    return `<button class="swatch" type="button" data-hex="${item.hex}" style="--swatch:${item.hex}" aria-label="Copy ${item.name} ${item.hex}, about ${item.share} percent of the sampled pixels"><span class="swatch-color"></span><span class="swatch-copy"><strong>${item.name}</strong><small>${item.hex} · ${item.share}%<br>${rgbText}</small></span></button>`;
  }

  function analyze() {
    if(!sourceCanvas.width)return;
    const region=regionForMode();
    renderSelection(region);
    currentPalette=extractPalette(region);
    currentInsight=buildInsight(currentPalette);
    document.querySelector('#observed-swatches').innerHTML=currentPalette.map(swatchButton).join('');
    document.querySelector('#palette-heading').textContent=currentInsight.mood;
    const scopeLabel=activeMode==='whole'?'photo':'selected area';
    document.querySelector('#palette-summary').textContent=`Visible pixels in your ${scopeLabel} read as ${currentInsight.light} and ${currentInsight.temperature}. Click any swatch to copy its HEX value.`;
    document.querySelector('#wall-reading').textContent=`The leading visible color is ${currentInsight.dominant.name.toLowerCase()} (${currentInsight.dominant.hex}), covering about ${currentInsight.dominant.share}% of the sampled pixels. Your ${scopeLabel} is ${currentInsight.temperature} with ${Math.round(currentInsight.l)}% lightness.`;
    document.querySelector('#sample-label').textContent=activeMode==='whole'?'Whole photo':'Selected area';
    document.querySelector('#reading-title').textContent=activeMode==='whole'?'Photo reading':'Selected-area reading';
    results.classList.remove('is-hidden');
  }

  function setMode(mode) {
    activeMode=mode;
    modeButtons.forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.mode===mode)));
    document.querySelector('#mode-help').textContent=mode==='whole'?'The palette includes everything visible in your photo.':'Drag a box over the exact surface you want to inspect.';
    if(mode!=='custom'||customRegion)analyze();
    else selectionBox.classList.add('is-hidden');
  }

  function loadImage(src, revoke=false) {
    const probe=new Image();
    probe.onload=()=>{
      sourceCanvas.width=probe.naturalWidth;sourceCanvas.height=probe.naturalHeight;
      sourceContext.drawImage(probe,0,0);
      image.src=src;image.classList.remove('is-hidden');placeholder.classList.add('is-hidden');stage.classList.remove('empty');photoLabel.textContent='Choose a different photo';
      controls.classList.remove('is-hidden');customRegion=null;setMode('whole');
      requestAnimationFrame(()=>renderSelection(regionForMode()));
      if(revoke)objectUrl=src;
    };
    probe.onerror=()=>showError('We could not read this image. Try a different JPG, PNG or WebP file.');
    probe.src=src;
  }

  function showError(message){error.textContent=message;error.classList.remove('is-hidden')}
  function clearError(){error.textContent='';error.classList.add('is-hidden')}
  function reset(){
    if(objectUrl){URL.revokeObjectURL(objectUrl);objectUrl=null}
    input.value='';sourceCanvas.width=0;sourceCanvas.height=0;image.removeAttribute('src');image.classList.add('is-hidden');placeholder.classList.remove('is-hidden');stage.classList.add('empty');photoLabel.textContent='Choose room photo';controls.classList.add('is-hidden');results.classList.add('is-hidden');selectionBox.classList.add('is-hidden');clearError();currentPalette=[];currentInsight=null;activeMode='whole';
  }

  function handleFile(file) {
    clearError();
    if(!file)return;
    if(!['image/jpeg','image/png','image/webp'].includes(file.type)){showError('Choose a JPG, PNG or WebP image.');return}
    if(file.size>15*1024*1024){showError('This image is larger than 15 MB. Choose a smaller copy.');return}
    if(objectUrl)URL.revokeObjectURL(objectUrl);
    objectUrl=URL.createObjectURL(file);
    loadImage(objectUrl,true);
  }

  input.addEventListener('change',()=>handleFile(input.files&&input.files[0]));
  [stage, document.querySelector('#upload-drop')].forEach(target=>{
    target.addEventListener('dragover',event=>{event.preventDefault();stage.classList.add('is-dragover')});
    target.addEventListener('dragleave',()=>stage.classList.remove('is-dragover'));
    target.addEventListener('drop',event=>{event.preventDefault();stage.classList.remove('is-dragover');handleFile(event.dataTransfer&&event.dataTransfer.files&&event.dataTransfer.files[0])});
  });
  document.querySelector('#reset-photo').addEventListener('click',reset);
  modeButtons.forEach(button=>button.addEventListener('click',()=>setMode(button.dataset.mode)));

  image.addEventListener('pointerdown',event=>{
    if(activeMode!=='custom')return;
    event.preventDefault();image.setPointerCapture(event.pointerId);const rect=image.getBoundingClientRect();
    dragging={x:clamp((event.clientX-rect.left)/rect.width,0,1),y:clamp((event.clientY-rect.top)/rect.height,0,1)};
    customRegion={x:dragging.x,y:dragging.y,w:0,h:0};renderSelection(customRegion);
  });
  image.addEventListener('pointermove',event=>{
    if(!dragging)return;const rect=image.getBoundingClientRect();const x=clamp((event.clientX-rect.left)/rect.width,0,1),y=clamp((event.clientY-rect.top)/rect.height,0,1);
    customRegion={x:Math.min(dragging.x,x),y:Math.min(dragging.y,y),w:Math.abs(x-dragging.x),h:Math.abs(y-dragging.y)};renderSelection(customRegion);
  });
  image.addEventListener('pointerup',event=>{
    if(!dragging)return;image.releasePointerCapture(event.pointerId);dragging=null;
    if(customRegion.w<.03||customRegion.h<.03){customRegion=null;announce('Drag a larger box over the surface.');return}
    analyze();
  });
  window.addEventListener('resize',()=>{if(sourceCanvas.width)renderSelection(regionForMode())});

  document.querySelector('#observed-swatches').addEventListener('click',async event=>{
    const button=event.target.closest('[data-hex]');if(!button)return;
    try{await navigator.clipboard.writeText(button.dataset.hex);announce(`${button.dataset.hex} copied.`)}catch{announce(`Copy ${button.dataset.hex}`)}
  });
  document.querySelector('#copy-palette').addEventListener('click',async()=>{
    const text=currentPalette.map(item=>item.hex).join(', ');if(!text)return;
    try{await navigator.clipboard.writeText(text);announce('Palette HEX colors copied.')}catch{announce(text)}
  });
  document.querySelector('#download-palette').addEventListener('click',()=>{
    if(!currentPalette.length)return;
    const canvas=document.createElement('canvas');canvas.width=1400;canvas.height=900;const ctx=canvas.getContext('2d');
    ctx.fillStyle='#f3f3f3';ctx.fillRect(0,0,1400,900);ctx.fillStyle='#ce1312';ctx.font='700 22px Arial';ctx.fillText('PAINTFU DECOR TOOLS',90,88);ctx.fillStyle='#111';ctx.font='800 60px Arial';ctx.fillText('Room color palette',90,172);ctx.fillStyle='#555';ctx.font='28px Arial';ctx.fillText(currentInsight.mood,90,222);
    const count=currentPalette.length,gap=16,sw=Math.min(220,(1220-gap*(count-1))/count),total=sw*count+gap*(count-1),startX=(1400-total)/2,startY=300;
    currentPalette.forEach((item,index)=>{const x=startX+index*(sw+gap);ctx.fillStyle=item.hex;ctx.fillRect(x,startY,sw,260);ctx.fillStyle='#fff';ctx.fillRect(x,startY+260,sw,105);ctx.fillStyle='#111';ctx.font='700 19px Arial';ctx.fillText(item.name,x+14,startY+298);ctx.fillStyle='#555';ctx.font='18px Arial';ctx.fillText(`${item.hex} · ${item.share}%`,x+14,startY+330)});
    const readingLabel=activeMode==='whole'?'Room reading':activeMode==='custom'?'Selected-area reading':'Wall reading';
    ctx.fillStyle='#111';ctx.font='700 25px Arial';ctx.fillText(`${readingLabel}: ${currentInsight.temperature}, ${currentInsight.light}, ${currentInsight.dominant.hex}`,90,730);ctx.fillStyle='#555';ctx.font='20px Arial';ctx.fillText('Colors are derived from visible pixels. Confirm paint colors with a physical sample.',90,782);ctx.fillText('paintfu.com/pages/tools',90,830);
    canvas.toBlob(blob=>{if(blob){downloadBlob(blob,'paintfu-room-color-palette.png');announce('Your palette is downloading.')}},'image/png');
  });
})();
